/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';

import { getReportingReportTemplatesUrl, matchAnyReportingViewTemplateUrl } from '@futrli/shared-services';
import { ReportTemplateDetail, ReportTemplateRequest } from '@futrli-sdk/predict';
import { UILoader } from '@futrli/ui-components';

import { AuthStoreState } from '../../core/types';
import { getResourceDisplayState } from '../get-resource-display-mode';
import { ReportBuilder } from '../report-builder';
import { ReportBuilderContextProvider } from '../report-builder.context';
import { TemplateView } from '../components/report-view/template-view';
import { usePredictionsInfoContext } from '../../forecast/detail/context/predictions-info-context';
import { useTemplatesContext } from '../templates.context';

interface Props {
  templateId: string;
}
export const EditTemplatePage = ({ templateId }: Props) => {
  const location = useLocation();
  const { resourceSharing } = useFlags();
  const { fetchTemplateById, patchTemplate } = useTemplatesContext();
  const [template, setTemplate] = useState<ReportTemplateDetail | undefined>();
  const [templateNotFound, setTemplateNotFound] = useState(false);
  const { organisationId = '', workspaceId = '' } = useParams();
  const { isConsolidation } = usePredictionsInfoContext();
  const currentUserId = useSelector((state: AuthStoreState) =>
    get(state, 'authReducers.authenticatedUser.attributes.sub', '')
  );
  const { displayMode: templateDisplayMode, shouldUseViewMode } = getResourceDisplayState(
    template,
    resourceSharing,
    currentUserId,
    !!matchAnyReportingViewTemplateUrl(location.pathname)
  );

  useEffect(() => {
    let isMounted = true;
    const getTemplate = async () => {
      const foundTemplate = await fetchTemplateById(templateId);
      if (isMounted) {
        if (foundTemplate) {
          setTemplate(foundTemplate);
        } else {
          setTemplateNotFound(true);
        }
      }
    };

    getTemplate();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const patchTemplateByType = (id: string, data: Partial<ReportTemplateRequest>) =>
    template?.kind === 'workspace' ? patchTemplate(id, data, true) : patchTemplate(id, data);

  if (templateNotFound || templateDisplayMode === 'no-access') {
    return <Navigate to={getReportingReportTemplatesUrl(workspaceId, organisationId, isConsolidation)} replace />;
  }

  return template ? (
    <ReportBuilderContextProvider
      report={template}
      patchReport={patchTemplateByType}
      isTemplate
      viewPage={shouldUseViewMode}
    >
      {shouldUseViewMode ? <TemplateView /> : <ReportBuilder />}
    </ReportBuilderContextProvider>
  ) : (
    <UILoader isFull />
  );
};
